import { retrieveContactQueryOptions } from "@/lib/data/react-query/contacts/contact";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { formatContactsName } from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useMatches } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";

const APP_NAME = "LawWorks";

const EXACT_ROUTE_TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/ai": "LawWorks AI",
  "/apps": "Apps",
  "/apps/app-library": "App library",
  "/apps/my-apps": "Connected apps",
  "/change-password": "Change password",
  "/charges": "Charges",
  "/contacts": "Contacts",
  "/file-share": "File share",
  "/files": "Files",
  "/forgot-password": "Forgot password",
  "/leads": "Intake",
  "/login": "Login",
  "/mass-torts": "Mass torts",
  "/matters": "Matters",
  "/matters/my-matters": "My matters",
  "/matters/settings": "Matter settings",
  "/matters/settings/costs": "Costs",
  "/matters/settings/files": "Default files",
  "/matters/settings/intake-templates": "Intake templates",
  "/matters/settings/litigation-plans": "Litigation plans",
  "/matters/settings/matter-types": "Incident types",
  "/matters/settings/roles": "Matter roles",
  "/matters/settings/scheduled-messages": "Scheduled messages",
  "/matters/settings/statuses": "Matter statuses",
  "/matters/settings/tags": "Matter tags",
  "/matters/settings/task-plans": "Task plans",
  "/matters/settings/phase-projects": "Matter phases",
  "/matters/settings/teams": "Matter teams",
  "/onboarding": "Onboarding",
  "/opportunities": "Opportunities",
  "/profile": "Profile",
  "/referrals": "Referrals",
  "/referrals/directory-profile": "Directory profile",
  "/referrals/settings": "Referral settings",
  "/referrals/settings/matter-types": "Referral types",
  "/referrals/settings/roles": "Referral roles",
  "/referrals/settings/statuses": "Referral statuses",
  "/referrals/settings/tags": "Referral tags",
  "/referrals/settings/task-plans": "Referral task plans",
  "/referrals/settings/teams": "Referral teams",
  "/firm/partner-firms": "Partner firms",
  "/firm/referral-profile": "Referral profile",
  "/register": "Register",
  "/reports": "Reports",
  "/reports/approaching-statutes": "Approaching statutes",
  "/reports/closed-matters": "Closed matters",
  "/reports/demand-summary": "Daily demands",
  "/reports/demands": "Demands",
  "/reports/matter-activity": "Matter activity",
  "/reports/matter-status-count": "Matters per status",
  "/reports/my-approaching-statutes": "My approaching statutes",
  "/reports/open-matters": "Open matters",
  "/reports/record-requests": "Record requests",
  "/reports/settlements": "Settlements",
  "/reports/settlement-summary": "Settlements",
  "/reports/attorney-summary": "Attorneys",
  "/reports/user-productivity": "User productivity",
  "/reset-password": "Reset password",
  "/settings": "Settings",
  "/settings/communication": "Communication settings",
  "/settings/contact": "Contact settings",
  "/settings/files": "File settings",
  "/settings/firm": "Firm settings",
  "/settings/index": "Settings",
  "/settings/intake": "Intake settings",
  "/settings/intake/lead-sources": "Lead sources",
  "/settings/intake/matter-types": "Intake types",
  "/settings/intake/roles": "Intake roles",
  "/settings/intake/statuses": "Intake statuses",
  "/settings/intake/tags": "Intake tags",
  "/settings/intake/task-plans": "Intake task plans",
  "/settings/intake/teams": "Intake teams",
  "/settings/matter-defaults": "Matter defaults",
  "/settings/matter-types": "Incident types",
  "/settings/templates": "Templates",
  "/settings/users": "User settings",
  "/sign": "Sign document",
  "/signature-requests": "Signature requests",
};

const MATTER_DETAIL_TITLES: Record<string, string> = {
  charges: "Charges",
  client: "Client",
  defendant: "Defendants",
  "other-parties": "Other parties",
};

const MATTER_SECTION_TITLES: Record<string, string> = {
  charges: "Charges",
  costs: "Costs",
  emails: "Emails",
  files: "Files",
  liens: "Liens",
  litigation: "Litigation",
  "matter-details": "Parties",
  "incident-details": "Incident details",
  "property-damage": "Property damage",
  notes: "Notes",
  updates: "Updates",
  "signature-requests": "Signature requests",
  settlements: "Settlements",
  tasks: "Tasks",
  "medical-treatments": "Medical treatments",
};

const MASS_TORT_SECTION_TITLES: Record<string, string> = {
  files: "Files",
  notes: "Notes",
  tasks: "Tasks",
};

const trimSlashes = (value: string) => value.replace(/^\/+|\/+$/g, "");

const toSentenceCase = (value: string) =>
  value
    .split("-")
    .filter(Boolean)
    .map((segment, index) =>
      index === 0
        ? segment.charAt(0).toUpperCase() + segment.slice(1)
        : segment,
    )
    .join(" ");

const normalizePath = (pathname: string) => {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
};

function resolveStaticRouteTitle(pathname: string) {
  return EXACT_ROUTE_TITLES[pathname];
}

function resolveMatterRouteTitle(pathname: string, matterTitle?: string) {
  const segments = trimSlashes(pathname).split("/");
  if (segments[0] !== "matters" || segments.length < 2) {
    return undefined;
  }

  const baseTitle = matterTitle?.trim() || "Matter";
  if (segments.length === 2) {
    return `${baseTitle} - Overview`;
  }

  const section = segments[2];
  if (section === "matter-details") {
    const tab = segments[3];
    const tabTitle = (tab && MATTER_DETAIL_TITLES[tab]) || "Parties";
    return `${baseTitle} - ${tabTitle}`;
  }

  const sectionTitle =
    MATTER_SECTION_TITLES[section] ||
    (section ? toSentenceCase(section) : "Overview");
  return `${baseTitle} - ${sectionTitle}`;
}

function resolveContactRouteTitle(pathname: string, title?: string) {
  const segments = trimSlashes(pathname).split("/");
  if (segments[0] !== "contacts" || segments.length < 2) {
    return undefined;
  }

  return `${title?.trim() || "Contact"} - Details`;
}

function resolveMassTortRouteTitle(pathname: string) {
  const segments = trimSlashes(pathname).split("/");
  if (segments[0] !== "mass-torts" || segments.length < 2) {
    return undefined;
  }

  if (segments.length === 2) {
    return "Mass tort - Overview";
  }

  const section = segments[2];
  const title =
    MASS_TORT_SECTION_TITLES[section] ||
    (section ? toSentenceCase(section) : "Overview");
  return `Mass tort - ${title}`;
}

function resolvePageTitle({
  pathname,
  matterTitle,
  title,
}: {
  pathname: string;
  matterTitle?: string;
  title?: string;
}) {
  return (
    resolveStaticRouteTitle(pathname) ||
    resolveMatterRouteTitle(pathname, matterTitle) ||
    resolveContactRouteTitle(pathname, title) ||
    resolveMassTortRouteTitle(pathname) ||
    APP_NAME
  );
}

export function DocumentTitleManager() {
  const pathname = useLocation({
    select: (location) => normalizePath(location.pathname),
  });
  const matches = useMatches();

  const matterId = (
    matches.find((match) => match.pathname.startsWith("/matters/")) as
      | { params?: { matterId?: string } }
      | undefined
  )?.params?.matterId;

  const contactId = (
    matches.find((match) => match.pathname.startsWith("/contacts/")) as
      | { params?: { contactId?: string } }
      | undefined
  )?.params?.contactId;

  const { data: matterData } = useQuery({
    ...retrieveMatterQueryOptions(matterId ?? ""),
    enabled: pathname.startsWith("/matters/") && Boolean(matterId),
    retry: false,
  });

  const { data: contactData } = useQuery({
    ...retrieveContactQueryOptions(contactId ?? ""),
    enabled: pathname.startsWith("/contacts/") && Boolean(contactId),
    retry: false,
  });

  const matterTitle = useMemo(() => {
    const data = matterData?.data;
    if (!data || Array.isArray(data)) return undefined;
    return typeof data.title === "string" && data.title.trim()
      ? data.title.trim()
      : undefined;
  }, [matterData]);

  const title = useMemo(() => {
    const data = contactData?.data;
    if (!data || Array.isArray(data)) return undefined;
    const formattedName = formatContactsName(data);
    if (typeof formattedName === "string" && formattedName.trim()) {
      return formattedName.trim();
    }
    if (
      typeof data.organizationName === "string" &&
      data.organizationName.trim()
    ) {
      return data.organizationName.trim();
    }
    if (typeof data.groupName === "string" && data.groupName.trim()) {
      return data.groupName.trim();
    }
    return undefined;
  }, [contactData]);

  useEffect(() => {
    const pageTitle = resolvePageTitle({
      pathname,
      matterTitle,
      title,
    });
    document.title =
      pageTitle === APP_NAME ? APP_NAME : `${pageTitle} | ${APP_NAME}`;
  }, [title, matterTitle, pathname]);

  return null;
}
